import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { getLocale } from '@/paraglide/runtime.js'
import * as m from '@/paraglide/messages.js'
import { fr, enUS } from 'react-day-picker/locale'
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import {
  DayPicker,
  DateLib,
  getDefaultClassNames,
  type DayButton,
  type Locale,
} from "react-day-picker"

import { buttonVariants } from "@/components/ui/button"
import { IconChevronLeft, IconChevronRight, IconChevronDown } from "@tabler/icons-react"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  locale = getLocale() === "fr" ? fr : enUS,
  labels,
  formatters,
  components,
  modifiersClassNames,
  variant = "default",
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: VariantProps<typeof buttonVariants>["variant"]
  variant?: "default" | "compact-timesheet"
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "group/calendar bg-background p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent",
        variant === "compact-timesheet" && "p-1 @min-[20rem]:p-3",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      locale={locale}
      labels={{
        labelNext: () => m.calendar_next_month(),
        labelPrevious: () => m.calendar_previous_month(),
        labelMonthDropdown: () => m.calendar_select_month(),
        labelYearDropdown: () => m.calendar_select_year(),
        labelNav: () => m.calendar_navigation(),
        labelWeekNumberHeader: () => m.calendar_week_number_header(),
        labelWeekNumber: (week) => m.timesheet_week_number({ week: String(week) }),
        labelDayButton: (date, modifiers, options, dateLib) => {
          let label = (dateLib ?? new DateLib(options)).format(date, 'PPPP');
          if (modifiers.today) label = m.calendar_day_today({ date: label });
          if (modifiers.selected) label = m.calendar_day_selected({ date: label });
          return label;
        },
        labelGridcell: (date, modifiers, options, dateLib) => {
          const label = (dateLib ?? new DateLib(options)).format(date, 'PPPP');
          return modifiers?.today ? m.calendar_day_today({ date: label }) : label;
        },
        ...labels,
      }}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code, { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month:
          variant === "compact-timesheet"
            ? "flex w-full flex-col gap-2"
            : cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative rounded-(--cell-radius)",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "absolute inset-0 bg-popover opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "font-medium select-none",
          captionLayout === "label"
            ? "text-sm"
            : "flex items-center gap-1 rounded-(--cell-radius) text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground",
          defaultClassNames.caption_label
        ),
        month_grid: cn("w-full border-collapse", defaultClassNames.month_grid),
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday:
          variant === "compact-timesheet"
            ? "min-w-0 flex-1 text-center type-micro font-medium text-muted-foreground"
            : cn(
                "flex-1 rounded-(--cell-radius) text-[0.8rem] font-normal text-muted-foreground select-none",
                defaultClassNames.weekday,
              ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-(--cell-size) select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-[0.8rem] text-muted-foreground select-none",
          defaultClassNames.week_number
        ),
        day:
          variant === "compact-timesheet"
            ? "group/day relative h-12 min-w-0 flex-1 p-0 text-center select-none"
            : cn(
                "group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)",
                props.showWeekNumber
                  ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)"
                  : "[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)",
                defaultClassNames.day,
              ),
        range_start: cn(
          "relative isolate z-0 rounded-l-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-muted",
          defaultClassNames.range_start
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn(
          "relative isolate z-0 rounded-r-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-muted",
          defaultClassNames.range_end
        ),
        today: cn(
          "rounded-(--cell-radius) bg-muted text-foreground data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      modifiersClassNames={{
        ...(variant === "compact-timesheet"
          ? {
              runStart: "ts-run ts-run-start",
              runMiddle: "ts-run",
              runEnd: "ts-run ts-run-end",
              offDay: "ts-offday",
            }
          : {}),
        ...modifiersClassNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <IconChevronLeft className={cn("size-4", className)} {...props} />
            )
          }

          if (orientation === "right") {
            return (
              <IconChevronRight className={cn("size-4", className)} {...props} />
            )
          }

          return (
            <IconChevronDown className={cn("size-4", className)} {...props} />
          )
        },
        DayButton: ({ ...props }) => (
          <CalendarDayButton {...props} {...(locale ? { locale } : {})} />
        ),
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

const calendarDayButtonVariants = cva('', {
  variants: {
    appearance: {
      default: '',
      compact: 'min-w-0 gap-0.5 rounded-md px-0',
    },
  },
  defaultVariants: { appearance: 'default' },
});

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  appearance = 'default',
  ...props
}: React.ComponentProps<typeof DayButton> &
  { locale?: Partial<Locale> } &
  VariantProps<typeof calendarDayButtonVariants>) {
  const defaultClassNames = getDefaultClassNames()
  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        buttonVariants({variant: "ghost", size: "icon"}),
        "relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 border-0 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50 data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-muted data-[range-middle=true]:text-foreground data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-foreground [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        calendarDayButtonVariants({ appearance }),
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
